import React, { useState, useEffect } from 'react';
import IcalSyncService from '../../Services/IcalSyncService';
import { PurpleButton } from '../Buttons/PurpleButton';
import { GreenButton } from '../Buttons/GreenButton';
import { BlackButton } from '../Buttons/BlackButton';
import { BlueButton } from '../Buttons/BlueButton';
import './IcalSyncManager.css';

/**
 * IcalSyncManager - Component for managing external calendar synchronization
 * 
 * Allows hosts to:
 * - Add iCal URLs from Airbnb, Booking.com, Vrbo, etc.
 * - View sync status for each calendar
 * - Manually trigger syncs
 * - Remove calendars
 * - View sync statistics
 */
const IcalSyncManager = ({ propertyId }) => {
  const [icalUrls, setIcalUrls] = useState([]);
  const [newIcalUrl, setNewIcalUrl] = useState('');
  const [newListingUrl, setNewListingUrl] = useState('');
  const [syncMethod, setSyncMethod] = useState('url'); // 'url' or 'ical' - URL is first option
  const [selectedPlatform, setSelectedPlatform] = useState('airbnb');
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [exportStats, setExportStats] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [showBlockedDates, setShowBlockedDates] = useState(false);
  const [loadingBlockedDates, setLoadingBlockedDates] = useState(false);
  const [scrapedDates, setScrapedDates] = useState(null); // Store scraped dates before blocking
  const [scrapingDates, setScrapingDates] = useState(false); // Loading state for scraping

  // Platform configurations
  const platforms = [
    { value: 'airbnb', label: 'Airbnb', color: '#FF5A5F' },
    { value: 'booking', label: 'Booking.com', color: '#003580' },
    { value: 'vrbo', label: 'Vrbo', color: '#003EBC' },
    { value: 'other', label: 'Other', color: '#666666' }
  ];

  // Load sync status on mount and when propertyId changes
  useEffect(() => {
    if (propertyId) {
      loadSyncStatus();
      loadExportStats();
      loadBlockedDates();
    }
  }, [propertyId]);

  /**
   * Load blocked dates for the property
   */
  const loadBlockedDates = async () => {
    if (!propertyId) return;
    
    setLoadingBlockedDates(true);
    try {
      const response = await IcalSyncService.getAllBlockedDates(propertyId);
      if (response.success && response.blockedDates) {
        setBlockedDates(response.blockedDates.externalBlocked || []);
        console.log(`📅 [Calendar Sync] Loaded ${response.blockedDates.externalBlocked?.length || 0} external blocked date ranges`);
      }
    } catch (err) {
      console.error('Error loading blocked dates:', err);
    } finally {
      setLoadingBlockedDates(false);
    }
  };

  /**
   * Load current sync status from backend
   */
  const loadSyncStatus = async () => {
    try {
      console.log(`📥 [IcalSyncManager] Loading sync status for property ${propertyId}`);
      const response = await IcalSyncService.getIcalSyncStatus(propertyId);
      
      console.log(`📥 [IcalSyncManager] Sync status response:`, {
        success: response.success,
        icalUrlsCount: response.icalSync?.icalUrls?.length || 0,
        externalBlockedDatesCount: response.icalSync?.externalBlockedDatesCount || 0,
        totalSyncCount: response.icalSync?.totalSyncCount || 0,
        lastAutoSync: response.icalSync?.lastAutoSync
      });
      
      if (response.success) {
        setIcalUrls(response.icalSync?.icalUrls || []);
        setSyncStatus(response.icalSync);
        
        console.log(`✅ [IcalSyncManager] Loaded ${response.icalSync?.icalUrls?.length || 0} iCal URLs`);
        console.log(`✅ [IcalSyncManager] Property has ${response.icalSync?.externalBlockedDatesCount || 0} external blocked dates`);
      }
    } catch (err) {
      console.error('❌ [IcalSyncManager] Error loading sync status:', err);
      // Don't show error for initial load
    }
  };

  /**
   * Load export statistics from backend
   */
  const loadExportStats = async () => {
    try {
      const response = await IcalSyncService.getExportStats(propertyId);
      if (response.success) {
        setExportStats(response.exportStats);
      }
    } catch (err) {
      console.error('Error loading export stats:', err);
    }
  };

  /**
   * Handle adding a new iCal URL
   */
  const handleAddIcalUrl = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!newIcalUrl.trim()) {
      setError('Please enter an iCal URL');
      return;
    }

    if (!newIcalUrl.startsWith('http://') && !newIcalUrl.startsWith('https://')) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }

    console.log(`📝 [Calendar Sync] Starting to add calendar URL for property ${propertyId}`);
    console.log(`📝 [Calendar Sync] URL: ${newIcalUrl}`);
    console.log(`📝 [Calendar Sync] Platform: ${selectedPlatform}`);

    setLoading(true);

    try {
      // First validate the URL
      console.log(`🔍 [Calendar Sync] Validating and fetching iCal URL...`);
      const validation = await IcalSyncService.validateIcalUrl(newIcalUrl);
      
      console.log(`🔍 [Calendar Sync] Validation result:`, {
        valid: validation.validation?.valid,
        error: validation.validation?.error,
        fetched: validation.validation?.valid ? '✅ Calendar fetched successfully' : '❌ Failed to fetch'
      });
      
      if (!validation.validation?.valid) {
        console.error(`❌ [Calendar Sync] Validation failed: ${validation.validation?.error || 'Could not fetch calendar'}`);
        setError(`Invalid iCal URL: ${validation.validation?.error || 'Could not fetch calendar'}`);
        setLoading(false);
        return;
      }

      console.log(`✅ [Calendar Sync] Calendar URL validated and fetched successfully`);
      console.log(`📤 [Calendar Sync] Adding calendar URL to backend...`);

      // Add the URL
      const response = await IcalSyncService.addIcalUrl(
        propertyId,
        newIcalUrl,
        selectedPlatform
      );

      console.log(`📥 [Calendar Sync] Add calendar response:`, {
        success: response.success,
        icalUrlId: response.icalUrlId,
        message: response.message,
        syncResult: response.syncResult ? {
          blockedDatesCount: response.syncResult.blockedDatesCount,
          successCount: response.syncResult.successCount,
          failCount: response.syncResult.failCount
        } : null
      });

      if (response.success) {
        const blockedCount = response.syncResult?.blockedDatesCount || 0;
        const syncSuccess = response.syncResult?.successCount || 0;
        const syncFail = response.syncResult?.failCount || 0;
        
        console.log(`✅ [Calendar Sync] Calendar URL added successfully to backend DB`);
        console.log(`📊 [Calendar Sync] Sync Results:`, {
          blockedDatesAdded: blockedCount,
          successfulSyncs: syncSuccess,
          failedSyncs: syncFail,
          status: blockedCount > 0 ? '✅ Blocked dates added to DB' : '⚠️ No blocked dates found'
        });
        
        if (blockedCount > 0) {
          console.log(`✅ [Calendar Sync] SUCCESS: ${blockedCount} blocked date${blockedCount !== 1 ? 's' : ''} added to database`);
        } else {
          console.log(`ℹ️ [Calendar Sync] Calendar added but no blocked dates found in the calendar`);
        }
        
        setSuccess(`✅ Calendar added successfully! Synced ${blockedCount} blocked date${blockedCount !== 1 ? 's' : ''}.`);
        setNewIcalUrl('');
        await loadSyncStatus(); // Reload to show new URL
      } else {
        console.error(`❌ [Calendar Sync] Failed to add calendar URL:`, response);
      }
    } catch (err) {
      console.error(`❌ [Calendar Sync] Error adding calendar URL:`, err);
      setError(err.message || 'Failed to add calendar');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle scraping blocked dates from URL (without saving)
   */
  const handleScrapeDates = async () => {
    setError(null);
    setSuccess(null);
    setScrapedDates(null);

    // Validation
    if (!newListingUrl.trim()) {
      setError('Please enter an Airbnb listing URL');
      return;
    }

    if (!newListingUrl.startsWith('http://') && !newListingUrl.startsWith('https://')) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }

    // Check if platform is supported
    if (selectedPlatform !== 'airbnb') {
      alert('🚀 We are expanding! Booking.com and Vrbo support coming soon.');
      return;
    }

    console.log(`📝 [URL Block Dates] Starting to scrape blocked dates from URL`);
    console.log(`📝 [URL Block Dates] URL: ${newListingUrl}`);

    setScrapingDates(true);

    try {
      // First validate the URL
      console.log(`🔍 [URL Block Dates] Validating listing URL...`);
      const validation = await IcalSyncService.validateListingUrl(newListingUrl);
      
      if (!validation.validation?.valid) {
        console.error(`❌ [URL Block Dates] Validation failed: ${validation.validation?.error || 'Invalid listing URL'}`);
        setError(`Invalid listing URL: ${validation.validation?.error || 'Could not validate URL'}`);
        setScrapingDates(false);
        return;
      }

      const detectedPlatform = validation.validation?.platform || selectedPlatform;
      if (detectedPlatform !== 'airbnb') {
        alert('🚀 We are expanding! Booking.com and Vrbo support coming soon.');
        setScrapingDates(false);
        return;
      }

      console.log(`✅ [URL Block Dates] Listing URL validated successfully (Platform: ${detectedPlatform})`);
      console.log(`📤 [URL Block Dates] Scraping calendar...`);

      // Scrape dates without saving to DB
      const scrapeResult = await IcalSyncService.getScrapedBlockedDates(newListingUrl, propertyId);

      console.log(`📥 [URL Block Dates] Scrape result:`, {
        success: scrapeResult.success,
        blockedDatesCount: scrapeResult.scrapeResult?.totalBlockedDates,
        dateRangesCount: scrapeResult.scrapeResult?.totalRanges
      });

      if (scrapeResult.success && scrapeResult.scrapeResult) {
        setScrapedDates({
          url: newListingUrl,
          platform: detectedPlatform,
          blockedDates: scrapeResult.scrapeResult.blockedDates || [],
          dateRanges: scrapeResult.scrapeResult.dateRanges || [],
          totalBlockedDates: scrapeResult.scrapeResult.totalBlockedDates || 0,
          totalRanges: scrapeResult.scrapeResult.totalRanges || 0
        });
        console.log(`✅ [URL Block Dates] Successfully scraped ${scrapeResult.scrapeResult.totalBlockedDates || 0} blocked dates`);
      } else {
        setError('Failed to scrape blocked dates. Please check the URL and try again.');
      }
    } catch (err) {
      console.error(`❌ [URL Block Dates] Error scraping dates:`, err);
      setError(err.message || 'Failed to scrape blocked dates');
    } finally {
      setScrapingDates(false);
    }
  };

  /**
   * Handle blocking the scraped dates (save to DB)
   */
  const handleBlockDates = async () => {
    if (!scrapedDates || !propertyId) {
      setError('No dates to block. Please scrape dates first.');
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      console.log(`💾 [URL Block Dates] Saving ${scrapedDates.totalBlockedDates} blocked dates to property ${propertyId}`);
      
      // Save dates to DB by scraping again with propertyId
      const response = await IcalSyncService.scrapeListingCalendar(scrapedDates.url, propertyId);

      if (response.success) {
        const blockedCount = response.totalBlockedDates || 0;
        const rangesCount = response.totalRanges || 0;
        
        console.log(`✅ [URL Block Dates] Successfully saved ${blockedCount} blocked dates to database`);
        
        setSuccess(`✅ Successfully blocked ${blockedCount} date${blockedCount !== 1 ? 's' : ''} (${rangesCount} range${rangesCount !== 1 ? 's' : ''})!`);
        setNewListingUrl('');
        setScrapedDates(null);
        await loadBlockedDates(); // Reload blocked dates
      } else {
        setError('Failed to save blocked dates. Please try again.');
      }
    } catch (err) {
      console.error(`❌ [URL Block Dates] Error blocking dates:`, err);
      setError(err.message || 'Failed to block dates');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle removing an iCal URL
   */
  const handleRemoveIcalUrl = async (icalUrlId) => {
    if (!window.confirm('Are you sure you want to remove this calendar sync? All blocked dates from this calendar will be removed.')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await IcalSyncService.removeIcalUrl(propertyId, icalUrlId);
      if (response.success) {
        setSuccess('✅ Calendar removed successfully');
        await loadSyncStatus();
      }
    } catch (err) {
      setError(err.message || 'Failed to remove calendar');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle manual sync trigger
   */
  const handleManualSync = async (icalUrlId = null) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log(`🔄 [Calendar Sync] ===== MANUAL SYNC TRIGGERED =====`);
      console.log(`🔄 [Calendar Sync] Property ID: ${propertyId}`);
      if (icalUrlId) {
        console.log(`🔄 [Calendar Sync] Syncing specific calendar URL: ${icalUrlId}`);
      } else {
        console.log(`🔄 [Calendar Sync] Syncing all calendars for this property`);
      }
      console.log(`📤 [Calendar Sync] Sending sync request to backend...`);
      
      const response = await IcalSyncService.triggerManualSync(propertyId, icalUrlId);
      
      console.log(`📥 [Calendar Sync] Backend sync response received:`, {
        success: response.success,
        message: response.message,
        blockedDatesCount: response.blockedDatesCount,
        successCount: response.successCount,
        failCount: response.failCount,
        errors: response.errors
      });
      
      if (response.success) {
        const count = response.blockedDatesCount || 0;
        const successCount = response.successCount || 0;
        const failCount = response.failCount || 0;
        
        console.log(`✅ [Calendar Sync] ===== SYNC COMPLETED SUCCESSFULLY =====`);
        console.log(`📊 [Calendar Sync] Sync Statistics:`, {
          totalBlockedDates: count,
          successfulSyncs: successCount,
          failedSyncs: failCount
        });
        
        if (count > 0) {
          console.log(`✅ [Calendar Sync] SUCCESS: ${count} blocked date${count !== 1 ? 's' : ''} added/updated in database`);
          console.log(`💾 [Calendar Sync] Blocked dates have been saved to backend DB`);
        } else {
          console.log(`ℹ️ [Calendar Sync] Sync completed but no new blocked dates found`);
        }
        
        if (failCount > 0) {
          console.warn(`⚠️ [Calendar Sync] Some calendars failed to sync: ${failCount} failure(s)`);
          if (response.errors && response.errors.length > 0) {
            console.warn(`⚠️ [Calendar Sync] Sync errors:`, response.errors);
          }
        }
        
        setSuccess(`✅ Sync complete! ${count} date${count !== 1 ? 's' : ''} blocked.`);
        
        console.log(`🔄 [Calendar Sync] Reloading sync status to refresh UI...`);
        await loadSyncStatus();
        await loadBlockedDates(); // Reload blocked dates after sync
        console.log(`✅ [Calendar Sync] Sync status reloaded and UI updated`);
      } else {
        console.error(`❌ [Calendar Sync] ===== SYNC FAILED =====`);
        console.error(`❌ [Calendar Sync] Error: ${response.message || 'Unknown error'}`);
        console.error(`❌ [Calendar Sync] Full response:`, response);
        setError(response.message || 'Failed to sync calendar');
      }
    } catch (err) {
      console.error(`❌ [Calendar Sync] ===== SYNC ERROR =====`);
      console.error(`❌ [Calendar Sync] Exception caught:`, err);
      console.error(`❌ [Calendar Sync] Error message: ${err.message}`);
      console.error(`❌ [Calendar Sync] Error stack:`, err.stack);
      setError(err.message || 'Failed to sync calendar');
    } finally {
      setLoading(false);
      console.log(`🏁 [Calendar Sync] Sync operation completed`);
    }
  };

  /**
   * Get badge for sync status
   */
  const getSyncStatusBadge = (status) => {
    const badges = {
      success: { text: 'Synced', color: 'green' },
      failed: { text: 'Failed', color: 'red' },
      pending: { text: 'Syncing...', color: 'yellow' },
      never: { text: 'Not Synced', color: 'gray' }
    };
    const badge = badges[status] || badges.never;
    return (
      <span className={`sync-badge sync-badge-${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  /**
   * Handle downloading iCal export file
   */
  const handleDownloadIcal = async () => {
    setExportLoading(true);
    setError(null);
    try {
      await IcalSyncService.downloadIcalFile(propertyId);
      setSuccess('iCal file downloaded successfully! Import it into your external platforms.');
    } catch (err) {
      setError(err.message);
    } finally {
      setExportLoading(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get platform info by value
   */
  const getPlatform = (value) => {
    return platforms.find(p => p.value === value) || platforms[platforms.length - 1];
  };

  return (
    <div className="ical-sync-manager px-2 sm:px-4 md:px-6">
      <div className="ical-header">
        <h3 className="text-lg sm:text-xl md:text-2xl">📅 Calendar Sync</h3>
      </div>
      
      <p className="description text-sm sm:text-base">
        Sync your calendar from Airbnb, Booking.com, or other platforms to prevent double bookings.
        Dates blocked on external platforms will automatically be unavailable on Coded Estate.
      </p>

      {/* Error/Success Messages */}
      {error && (
        <div className="alert alert-error">
          <span>❌</span>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Add Calendar Section - Method Selection */}
      <div className="add-ical-section">
            <h4 className="text-base sm:text-lg md:text-xl">Add Calendar</h4>
            
            {/* Method Toggle */}
            <div className="sync-method-toggle flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
              <button
                type="button"
                onClick={() => {
                  setSyncMethod('url');
                  setScrapedDates(null);
                  setError(null);
                }}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm md:text-base border border-gray-300 rounded-md transition-all ${
                  syncMethod === 'url' 
                    ? 'bg-purple-600 text-white border-purple-600' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                🔗 URL Block Dates
              </button>
              <button
                type="button"
                onClick={() => {
                  setSyncMethod('ical');
                  setScrapedDates(null);
                  setError(null);
                }}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm md:text-base border border-gray-300 rounded-md transition-all ${
                  syncMethod === 'ical' 
                    ? 'bg-purple-600 text-white border-purple-600' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                📅 iCal URL
              </button>
            </div>

            {syncMethod === 'url' ? (
              /* URL Block Dates Method */
              <div>
                <div className="input-group flex-col sm:flex-row gap-2 sm:gap-3">
                  <select 
                    value={selectedPlatform} 
                    onChange={(e) => {
                      const platform = e.target.value;
                      setSelectedPlatform(platform);
                      if (platform !== 'airbnb') {
                        alert('🚀 We are expanding! Booking.com and Vrbo support coming soon.');
                        setSelectedPlatform('airbnb');
                      }
                    }}
                    className="platform-select w-full sm:w-auto sm:min-w-[140px] text-sm sm:text-base"
                    disabled={loading || scrapingDates}
                  >
                    <option value="airbnb">Airbnb</option>
                    <option value="booking">Booking.com</option>
                    <option value="vrbo">Vrbo</option>
                  </select>
                  
                  <input
                    type="url"
                    placeholder="Enter your Airbnb listing URL to block dates"
                    value={newListingUrl}
                    onChange={(e) => setNewListingUrl(e.target.value)}
                    className="ical-input w-full text-sm sm:text-base"
                    disabled={loading || scrapingDates}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !scrapingDates) {
                        handleScrapeDates();
                      }
                    }}
                  />
                  
                  <PurpleButton
                    text={scrapingDates ? '⏳ Extracting...' : '🔍 Extract Dates'}
                    onClick={handleScrapeDates}
                    disabled={!newListingUrl || loading || scrapingDates}
                    className="w-full sm:w-auto"
                  />
                </div>

                {/* Show scraped dates */}
                {scrapedDates && (
                  <div className="mt-4 sm:mt-5 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-300">
                    <h5 className="mt-0 mb-3 sm:mb-4 text-sm sm:text-base md:text-lg font-semibold">
                      📅 Extracted Blocked Dates
                    </h5>
                    <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                      Found <strong>{scrapedDates.totalBlockedDates}</strong> blocked date{scrapedDates.totalBlockedDates !== 1 ? 's' : ''} 
                      {' '}in <strong>{scrapedDates.totalRanges}</strong> range{scrapedDates.totalRanges !== 1 ? 's' : ''}
                    </p>
                    
                    {/* Show date ranges */}
                    {scrapedDates.dateRanges && scrapedDates.dateRanges.length > 0 && (
                      <div className="max-h-[200px] sm:max-h-[300px] overflow-y-auto mb-3 sm:mb-4">
                        {scrapedDates.dateRanges.slice(0, 10).map((range, idx) => {
                          const start = new Date(range.startDate);
                          const end = new Date(range.endDate);
                          const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                          return (
                            <div key={idx} className="text-xs sm:text-sm p-2 sm:p-3 mb-2 border border-gray-300 rounded bg-white">
                              <div className="font-semibold mb-1">
                                📅 {start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} → {end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                              <div className="text-gray-600 text-xs">
                                {nights} night{nights !== 1 ? 's' : ''} blocked
                              </div>
                            </div>
                          );
                        })}
                        {scrapedDates.dateRanges.length > 10 && (
                          <div className="text-xs text-gray-500 mt-2 italic">
                            ... and {scrapedDates.dateRanges.length - 10} more range{scrapedDates.dateRanges.length - 10 !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Block These Dates Button */}
                    <PurpleButton
                      text={loading ? '⏳ Blocking...' : '✅ Block These Dates'}
                      onClick={handleBlockDates}
                      disabled={loading}
                      className="w-full sm:w-auto"
                    />
                  </div>
                )}
              </div>
            ) : syncMethod === 'ical' ? (
              /* iCal URL Method */
              <div className="input-group flex-col sm:flex-row gap-2 sm:gap-3">
                <select 
                  value={selectedPlatform} 
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  className="platform-select w-full sm:w-auto sm:min-w-[140px] text-sm sm:text-base"
                  disabled={loading}
                >
                  {platforms.map(platform => (
                    <option key={platform.value} value={platform.value}>
                      {platform.label}
                    </option>
                  ))}
                </select>
                
                <input
                  type="url"
                  placeholder="Paste iCal URL here (e.g., https://www.airbnb.com/calendar/ical/...)"
                  value={newIcalUrl}
                  onChange={(e) => setNewIcalUrl(e.target.value)}
                  className="ical-input w-full text-sm sm:text-base"
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddIcalUrl();
                    }
                  }}
                />
                
                <PurpleButton
                  text={loading ? '⏳ Adding...' : '➕ Add Calendar'}
                  onClick={handleAddIcalUrl}
                  disabled={!newIcalUrl || loading}
                  className="w-full sm:w-auto"
                />
              </div>
            ) : null}
          </div>

          {/* Connected Calendars - Only show for iCal URL method */}
          {syncMethod === 'ical' && (
            <div className="connected-calendars">
              <h4 className="text-base sm:text-lg md:text-xl">Connected Calendars ({icalUrls.length})</h4>
              
              {icalUrls.length === 0 ? (
                <div className="no-calendars">
                  <p>📭 No calendars connected yet.</p>
                  <p className="hint">Add your first calendar above to start syncing dates!</p>
                </div>
              ) : (
                <div className="calendar-list">
                  {icalUrls.map((icalUrl) => {
                    const platform = getPlatform(icalUrl.platform);
                    return (
                      <div key={icalUrl._id} className="calendar-item">
                        <div className="calendar-header">
                          <span 
                            className="platform-badge" 
                            style={{ backgroundColor: platform.color }}
                          >
                            {platform.label}
                          </span>
                          {getSyncStatusBadge(icalUrl.syncStatus)}
                        </div>
                        
                        <div className="calendar-info">
                          <p className="calendar-url" title={icalUrl.url}>
                            🔗 {icalUrl.url.substring(0, 60)}...
                          </p>
                          <div className="calendar-meta">
                            <span>📅 Last sync: {formatDate(icalUrl.lastSync)}</span>
                            {icalUrl.lastSyncAttempt && (
                              <span>🕒 Last attempt: {formatDate(icalUrl.lastSyncAttempt)}</span>
                            )}
                          </div>
                          {icalUrl.syncError && (
                            <p className="sync-error">
                              ⚠️ Error: {icalUrl.syncError}
                            </p>
                          )}
                        </div>

                        <div className="calendar-actions flex-col sm:flex-row gap-2 sm:gap-3">
                          <GreenButton
                            text={loading ? '⏳ Syncing...' : '🔄 Sync Now'}
                            onClick={() => !loading && handleManualSync(icalUrl._id)}
                            className="w-full sm:w-auto"
                          />
                          <BlackButton
                            text="🗑️ Remove"
                            onClick={() => handleRemoveIcalUrl(icalUrl._id)}
                            disabled={loading}
                            className="w-full sm:w-auto"
                          />
                        </div>
                        
                        {/* Show blocked dates for this calendar */}
                        {icalUrl.syncStatus === 'success' && (
                          <div className="blocked-dates-preview" style={{ marginTop: '10px', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
                            <button
                              onClick={() => {
                                const currentUrl = icalUrl._id;
                                setShowBlockedDates(showBlockedDates === currentUrl ? null : currentUrl);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#666',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontWeight: '500'
                              }}
                            >
                              {showBlockedDates === icalUrl._id ? '▼' : '▶'} View Blocked Dates
                              {loadingBlockedDates && <span style={{ fontSize: '10px' }}>(Loading...)</span>}
                            </button>
                            {showBlockedDates === icalUrl._id && (
                              <div style={{ marginTop: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                                {blockedDates.filter(bd => {
                                  // Match by URL or platform
                                  return bd.source === icalUrl.url || 
                                         (bd.platform === icalUrl.platform && icalUrl.url.includes(bd.source?.split('/')[2] || ''));
                                }).length > 0 ? (
                                  <div>
                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>
                                      Found {blockedDates.filter(bd => bd.source === icalUrl.url || bd.platform === icalUrl.platform).length} blocked date range(s)
                                    </div>
                                    {blockedDates
                                      .filter(bd => {
                                        return bd.source === icalUrl.url || 
                                               (bd.platform === icalUrl.platform && icalUrl.url.includes(bd.source?.split('/')[2] || ''));
                                      })
                                      .slice(0, 20)
                                      .map((blocked, idx) => {
                                        const start = new Date(blocked.startDate);
                                        const end = new Date(blocked.endDate);
                                        const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                                        return (
                                          <div key={idx} style={{ 
                                            fontSize: '11px', 
                                            padding: '8px', 
                                            marginBottom: '5px',
                                            border: '1px solid #ddd', 
                                            borderRadius: '3px',
                                            background: '#fff'
                                          }}>
                                            <div style={{ fontWeight: '600', marginBottom: '3px' }}>
                                              📅 {start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} → {end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </div>
                                            <div style={{ color: '#666', fontSize: '10px' }}>
                                              {nights} night{nights !== 1 ? 's' : ''} blocked
                                              {blocked.eventTitle && <span> • {blocked.eventTitle}</span>}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    {blockedDates.filter(bd => bd.source === icalUrl.url || bd.platform === icalUrl.platform).length > 20 && (
                                      <div style={{ fontSize: '10px', color: '#888', marginTop: '5px', fontStyle: 'italic' }}>
                                        ... and {blockedDates.filter(bd => bd.source === icalUrl.url || bd.platform === icalUrl.platform).length - 20} more
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '11px', color: '#888', fontStyle: 'italic', padding: '10px' }}>
                                    No blocked dates found for this calendar yet. Click "Sync Now" to fetch blocked dates.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Export Coded Estate Calendar Section */}
          <div className="export-section">
            <div className="export-header">
              <h4 className="text-base sm:text-lg md:text-xl">📤 Export Coded Estate Calendar</h4>
              <p className="export-description text-sm sm:text-base">
                Download your Coded Estate bookings as an iCal file to import into external platforms (Airbnb, Booking.com, Vrbo).
              </p>
            </div>
            
            <div className="export-actions-wrapper">
              <PurpleButton
                text={exportLoading ? '⏳ Downloading...' : '📥 Download iCal File'}
                onClick={handleDownloadIcal}
                disabled={exportLoading}
                className="w-full sm:w-auto"
              />
            </div>

            {exportStats && (
              <div className="export-stats">
                <h5>📊 Export Statistics</h5>
                <div className="export-stats-grid">
                  <div className="export-stat-item">
                    <span className="export-stat-value">{exportStats.totalBookings || 0}</span>
                    <span className="export-stat-label">Total Bookings</span>
                  </div>
                  <div className="export-stat-item">
                    <span className="export-stat-value">{exportStats.upcomingBookings || 0}</span>
                    <span className="export-stat-label">Upcoming</span>
                  </div>
                  <div className="export-stat-item">
                    <span className="export-stat-value">{exportStats.exportUrl ? '✅' : '❌'}</span>
                    <span className="export-stat-label">Export Ready</span>
                  </div>
                </div>
              </div>
            )}

            <div className="export-instructions">
              <h5>📋 How to Import into External Platforms:</h5>
              <div className="platform-instructions-list">
                <div className="instruction-item">
                  <span className="instruction-icon">🏠</span>
                  <div className="instruction-content">
                    <strong>Airbnb:</strong> Calendar → Availability settings → Import calendar → Upload file
                  </div>
                </div>
                <div className="instruction-item">
                  <span className="instruction-icon">🌐</span>
                  <div className="instruction-content">
                    <strong>Booking.com:</strong> Extranet → Calendar → Import calendar → Upload file
                  </div>
                </div>
                <div className="instruction-item">
                  <span className="instruction-icon">🏖️</span>
                  <div className="instruction-content">
                    <strong>Vrbo:</strong> Calendar → Import/Export → Import calendar → Upload file
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sync Statistics */}
          {syncStatus && icalUrls.length > 0 && (
            <div className="sync-stats">
              <h4 className="text-base sm:text-lg md:text-xl">📊 Import Statistics</h4>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-value">{syncStatus.externalBlockedDatesCount || 0}</span>
                  <span className="stat-label">Blocked Dates</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{syncStatus.totalSyncCount || 0}</span>
                  <span className="stat-label">Total Syncs</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{formatDate(syncStatus.lastAutoSync)}</span>
                  <span className="stat-label">Last Auto-Sync</span>
                </div>
              </div>
              
              <div className="auto-sync-info">
                <p>
                  ⏰ <strong>Automatic sync</strong> runs every 4 hours to keep your calendar up to date.
                  {syncStatus.syncSettings?.autoSync === false && (
                    <span className="warning"> (Currently disabled)</span>
                  )}
                </p>
              </div>
            </div>
          )}
    </div>
  );
};

export default IcalSyncManager;





